/**
 * Slack mrkdwn → readable text / render segments.
 * Pure helpers (no I/O). Used by the SPA and mirrored in Functions/server BFF.
 */

export type SlackNameMap = Readonly<Record<string, string>>;

export type SlackTextSegment =
  | { type: 'text'; value: string }
  | { type: 'mention'; userId: string; label: string }
  | { type: 'channel'; channelId: string; label: string }
  | { type: 'link'; href: string; label: string }
  | { type: 'bold'; children: SlackTextSegment[] }
  | { type: 'italic'; children: SlackTextSegment[] }
  | { type: 'strike'; children: SlackTextSegment[] }
  | { type: 'code'; value: string }
  | { type: 'codeBlock'; value: string; language?: string }
  | { type: 'br' }
  | { type: 'list'; ordered: boolean; items: SlackTextSegment[][] };

const USER_MENTION_RE = /<@([UW][A-Z0-9]+)(?:\|([^>]+))?>/g;
const CHANNEL_MENTION_RE = /<#([CG][A-Z0-9]+)(?:\|([^>]+))?>/g;
const LABELED_LINK_RE = /<(https?:\/\/[^|>]+)\|([^>]+)>/g;
const BARE_LINK_RE = /<(https?:\/\/[^>]+)>/g;
const SPECIAL_CMD_RE = /<!(?:here|channel|everyone)(?:\|[^>]*)?>/g;
const SUBTEAM_RE = /<!subteam\^[^|>]+(?:\|([^>]+))?>/g;
const CODE_FENCE_RE = /```([^\n`]*)\n?([\s\S]*?)```/g;
const INLINE_CODE_RE = /`([^`\n]+)`/g;
const PLACEHOLDER_RE = /\u0000(\d+)\u0000/g;

/** Jira-style issue keys (DEV-3046, FI-12, …). */
export const JIRA_KEY_RE = /\b([A-Z][A-Z0-9]+-\d+)\b/g;
/** Explicit PR references: PR #275, pull request #104, PR 275. */
export const PR_REF_RE = /\b(?:PR|pull request)\s*#?\s*(\d+)\b/gi;

export interface ExternalLinkConfig {
  jiraBrowseBase: string;
  githubPrBase?: string;
}

export const DEFAULT_EXTERNAL_LINKS: ExternalLinkConfig = {
  jiraBrowseBase: 'https://jira.genlogs.io/browse',
  githubPrBase: 'https://github.com/pulls?q=is%3Apr+',
};

function collapseWhitespace(value: string): string {
  return value.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').replace(/[ \t]{2,}/g, ' ').trim();
}

/**
 * Strip Slack emphasis / code mrkdwn markers while keeping inner text.
 * Order matters: code fences first, then inline code, then bold/italic/strike.
 */
export function stripSlackEmphasis(text: string): string {
  let out = text;
  out = out.replace(/```(?:[\w+-]*)?\n?([\s\S]*?)```/g, '$1');
  out = out.replace(/`([^`]+)`/g, '$1');
  // Bold / italic / strike — allow nested spaces around mentions already expanded.
  out = out.replace(/\*([^*\n]+)\*/g, '$1');
  out = out.replace(/_([^_\n]+)_/g, '$1');
  out = out.replace(/~([^~\n]+)~/g, '$1');
  return out;
}

/**
 * Resolve Slack entities (<@U>, <#C|name>, <url|label>) into plain text using
 * an optional user-id → display-name map.
 */
export function formatSlackMrkdwn(text: string, names: SlackNameMap = {}): string {
  if (!text) return '';

  let out = text;
  out = out.replace(USER_MENTION_RE, (_m, id: string, label?: string) => {
    const resolved = (label && label.trim()) || names[id] || id;
    return `@${resolved}`;
  });
  out = out.replace(CHANNEL_MENTION_RE, (_m, _id: string, label?: string) => {
    const name = (label && label.trim()) || 'channel';
    return name.startsWith('#') ? name : `#${name}`;
  });
  out = out.replace(LABELED_LINK_RE, (_m, _url: string, label: string) => label);
  out = out.replace(BARE_LINK_RE, (_m, url: string) => url);
  out = out.replace(SPECIAL_CMD_RE, (m) => {
    if (m.includes('here')) return '@here';
    if (m.includes('channel')) return '@channel';
    return '@everyone';
  });
  out = out.replace(SUBTEAM_RE, (_m, label?: string) => (label ? `@${label}` : '@group'));
  out = stripSlackEmphasis(out);
  return collapseWhitespace(out);
}

type EmphasisKind = 'bold' | 'italic' | 'strike';

/**
 * Parse *bold*, _italic_, and ~strike~ into nested segments.
 * Placeholder tokens (\u0000N\u0000) stay opaque so emphasis can wrap entities.
 */
function parseInlineFormatting(text: string): SlackTextSegment[] {
  if (!text) return [];

  type Hit = { start: number; end: number; kind: EmphasisKind; inner: string };
  const patterns: Array<{ kind: EmphasisKind; source: string }> = [
    { kind: 'bold', source: '\\*([^*\\n]+)\\*' },
    { kind: 'italic', source: '_([^_\\n]+)_' },
    { kind: 'strike', source: '~([^~\\n]+)~' },
  ];

  let best: Hit | null = null;
  for (const { kind, source } of patterns) {
    for (const match of text.matchAll(new RegExp(source, 'g'))) {
      const start = match.index ?? 0;
      const end = start + match[0].length;
      if (!best || start < best.start || (start === best.start && end < best.end)) {
        best = { start, end, kind, inner: match[1] };
      }
    }
  }

  if (!best) return [{ type: 'text', value: text }];

  const out: SlackTextSegment[] = [];
  if (best.start > 0) {
    out.push({ type: 'text', value: text.slice(0, best.start) });
  }
  out.push({
    type: best.kind,
    children: parseInlineFormatting(best.inner),
  });
  out.push(...parseInlineFormatting(text.slice(best.end)));
  return out;
}

function expandPlaceholders(
  segments: SlackTextSegment[],
  placeholders: SlackTextSegment[]
): SlackTextSegment[] {
  const out: SlackTextSegment[] = [];
  for (const segment of segments) {
    if (segment.type === 'text') {
      const parts = segment.value.split(PLACEHOLDER_RE);
      for (let i = 0; i < parts.length; i += 1) {
        if (i % 2 === 1) {
          const ph = placeholders[Number(parts[i])];
          if (ph) out.push(ph);
          continue;
        }
        if (parts[i]) out.push({ type: 'text', value: parts[i] });
      }
      continue;
    }
    if (segment.type === 'bold' || segment.type === 'italic' || segment.type === 'strike') {
      out.push({
        ...segment,
        children: expandPlaceholders(segment.children, placeholders),
      });
      continue;
    }
    if (segment.type === 'list') {
      out.push({
        ...segment,
        items: segment.items.map((item) => expandPlaceholders(item, placeholders)),
      });
      continue;
    }
    out.push(segment);
  }
  return out;
}

/** Split a segment list into visual lines on bare newlines inside text nodes. */
function segmentsToLines(segments: SlackTextSegment[]): SlackTextSegment[][] {
  const lines: SlackTextSegment[][] = [[]];
  const push = (seg: SlackTextSegment) => {
    lines[lines.length - 1].push(seg);
  };

  for (const segment of segments) {
    if (segment.type === 'text') {
      const parts = segment.value.split('\n');
      for (let i = 0; i < parts.length; i += 1) {
        if (i > 0) lines.push([]);
        if (parts[i]) push({ type: 'text', value: parts[i] });
      }
      continue;
    }
    push(segment);
  }
  return lines;
}

function tryParseListLine(
  line: SlackTextSegment[]
): { ordered: boolean; content: SlackTextSegment[] } | null {
  if (line.length === 0) return null;
  const first = line[0];
  if (first.type !== 'text') return null;

  const bullet = first.value.match(/^(?:•|-)\s+(.*)$/);
  if (bullet) {
    const rest = bullet[1];
    const content: SlackTextSegment[] = rest
      ? [{ type: 'text', value: rest }, ...line.slice(1)]
      : [...line.slice(1)];
    return { ordered: false, content };
  }

  const ordered = first.value.match(/^\d+\.\s+(.*)$/);
  if (ordered) {
    const rest = ordered[1];
    const content: SlackTextSegment[] = rest
      ? [{ type: 'text', value: rest }, ...line.slice(1)]
      : [...line.slice(1)];
    return { ordered: true, content };
  }

  return null;
}

/**
 * Detect bullet (`• ` / `- ` only — not `*`) and ordered (`1. `) lists;
 * insert `br` between non-list lines.
 */
function parseBlockStructure(segments: SlackTextSegment[]): SlackTextSegment[] {
  const lines = segmentsToLines(segments);
  if (lines.length === 0) return [];

  const out: SlackTextSegment[] = [];
  let i = 0;
  let needsBr = false;

  while (i < lines.length) {
    const listStart = tryParseListLine(lines[i]);
    if (listStart) {
      const ordered = listStart.ordered;
      const items: SlackTextSegment[][] = [listStart.content];
      i += 1;
      while (i < lines.length) {
        const next = tryParseListLine(lines[i]);
        if (!next || next.ordered !== ordered) break;
        items.push(next.content);
        i += 1;
      }
      if (needsBr) out.push({ type: 'br' });
      out.push({ type: 'list', ordered, items });
      needsBr = true;
      continue;
    }

    const line = lines[i];
    i += 1;
    if (line.length === 0) {
      needsBr = true;
      continue;
    }
    if (needsBr) out.push({ type: 'br' });
    out.push(...line);
    needsBr = true;
  }

  return out;
}

/**
 * Tokenize Slack mrkdwn into render segments (emphasis, mentions, channels, links, lists).
 * Does NOT strip emphasis — styled segments are preserved for SlackRichText.
 */
export function parseSlackMrkdwn(text: string, names: SlackNameMap = {}): SlackTextSegment[] {
  if (!text) return [];

  const placeholders: SlackTextSegment[] = [];
  const push = (segment: SlackTextSegment): string => {
    const idx = placeholders.length;
    placeholders.push(segment);
    return `\u0000${idx}\u0000`;
  };

  let work = text;

  // 1) Code fences / inline code / entities → opaque placeholders
  work = work.replace(CODE_FENCE_RE, (_m, lang: string, code: string) => {
    const language = (lang || '').trim() || undefined;
    const value = code.replace(/^\n/, '').replace(/\n$/, '');
    return push({ type: 'codeBlock', value, language });
  });
  work = work.replace(INLINE_CODE_RE, (_m, code: string) => push({ type: 'code', value: code }));
  work = work.replace(USER_MENTION_RE, (_m, id: string, label?: string) => {
    const resolved = (label && label.trim()) || names[id] || id;
    return push({ type: 'mention', userId: id, label: resolved });
  });
  work = work.replace(CHANNEL_MENTION_RE, (_m, id: string, label?: string) => {
    const name = (label && label.trim()) || 'channel';
    return push({
      type: 'channel',
      channelId: id,
      label: name.startsWith('#') ? name : `#${name}`,
    });
  });
  work = work.replace(LABELED_LINK_RE, (_m, url: string, label: string) =>
    push({ type: 'link', href: url, label })
  );
  work = work.replace(BARE_LINK_RE, (_m, url: string) =>
    push({ type: 'link', href: url, label: url })
  );
  work = work.replace(SPECIAL_CMD_RE, (m) => {
    const label = m.includes('here') ? '@here' : m.includes('channel') ? '@channel' : '@everyone';
    return push({ type: 'text', value: label });
  });
  work = work.replace(SUBTEAM_RE, (_m, label?: string) =>
    push({ type: 'text', value: label ? `@${label}` : '@group' })
  );

  // 2) Inline emphasis (placeholders opaque) → 3) expand → 4) lists / br
  const inline = parseInlineFormatting(work);
  const expanded = expandPlaceholders(inline, placeholders);
  return parseBlockStructure(expanded);
}

/**
 * Expand plain-text segments with Jira keys, PR refs, and bare http(s) URLs.
 * Recurses into bold/italic/strike children and list items.
 */
export function enrichSegmentsWithExternalLinks(
  segments: SlackTextSegment[],
  config: ExternalLinkConfig = DEFAULT_EXTERNAL_LINKS
): SlackTextSegment[] {
  const out: SlackTextSegment[] = [];
  for (const segment of segments) {
    if (segment.type === 'text') {
      out.push(...linkifyPlainText(segment.value, config));
      continue;
    }
    if (segment.type === 'bold' || segment.type === 'italic' || segment.type === 'strike') {
      out.push({
        ...segment,
        children: enrichSegmentsWithExternalLinks(segment.children, config),
      });
      continue;
    }
    if (segment.type === 'list') {
      out.push({
        ...segment,
        items: segment.items.map((item) => enrichSegmentsWithExternalLinks(item, config)),
      });
      continue;
    }
    out.push(segment);
  }
  return mergeAdjacentText(out);
}

function linkifyPlainText(text: string, config: ExternalLinkConfig): SlackTextSegment[] {
  if (!text) return [];

  type Hit = { start: number; end: number; segment: SlackTextSegment };
  const hits: Hit[] = [];

  const jira = new RegExp(JIRA_KEY_RE.source, 'g');
  for (const match of text.matchAll(jira)) {
    const key = match[1];
    const start = match.index ?? 0;
    hits.push({
      start,
      end: start + match[0].length,
      segment: {
        type: 'link',
        href: `${config.jiraBrowseBase.replace(/\/+$/, '')}/${key}`,
        label: key,
      },
    });
  }

  const pr = new RegExp(PR_REF_RE.source, 'gi');
  for (const match of text.matchAll(pr)) {
    const num = match[1];
    const start = match.index ?? 0;
    const href = config.githubPrBase?.includes('pulls?')
      ? `${config.githubPrBase}${encodeURIComponent(num)}`
      : `${(config.githubPrBase ?? '').replace(/\/+$/, '')}/pull/${num}`;
    hits.push({
      start,
      end: start + match[0].length,
      segment: { type: 'link', href, label: match[0].trim() },
    });
  }

  const urlRe = /https?:\/\/[^\s<>)"]+/g;
  for (const match of text.matchAll(urlRe)) {
    const start = match.index ?? 0;
    let href = match[0];
    // Trim trailing punctuation commonly glued to URLs.
    href = href.replace(/[.,;:!?)]+$/, '');
    hits.push({
      start,
      end: start + href.length,
      segment: { type: 'link', href, label: href },
    });
  }

  hits.sort((a, b) => a.start - b.start || b.end - a.end);
  const selected: Hit[] = [];
  let cursor = 0;
  for (const hit of hits) {
    if (hit.start < cursor) continue;
    selected.push(hit);
    cursor = hit.end;
  }

  if (selected.length === 0) return [{ type: 'text', value: text }];

  const segments: SlackTextSegment[] = [];
  let at = 0;
  for (const hit of selected) {
    if (hit.start > at) {
      segments.push({ type: 'text', value: text.slice(at, hit.start) });
    }
    segments.push(hit.segment);
    at = hit.end;
  }
  if (at < text.length) {
    segments.push({ type: 'text', value: text.slice(at) });
  }
  return segments;
}

function mergeAdjacentText(segments: SlackTextSegment[]): SlackTextSegment[] {
  const out: SlackTextSegment[] = [];
  for (const segment of segments) {
    const prev = out[out.length - 1];
    if (segment.type === 'text' && prev?.type === 'text') {
      prev.value += segment.value;
    } else {
      out.push(segment.type === 'text' ? { ...segment } : segment);
    }
  }
  return out;
}

/**
 * Full pipeline: mrkdwn → segments with external links ready for React.
 */
export function slackTextToSegments(
  text: string,
  names: SlackNameMap = {},
  config: ExternalLinkConfig = DEFAULT_EXTERNAL_LINKS
): SlackTextSegment[] {
  return enrichSegmentsWithExternalLinks(parseSlackMrkdwn(text, names), config);
}

/** Collect <@U…> ids from raw Slack text. */
export function extractMentionedUserIds(text: string): string[] {
  const ids: string[] = [];
  const re = new RegExp(USER_MENTION_RE.source, 'g');
  for (const match of text.matchAll(re)) {
    ids.push(match[1]);
  }
  return ids;
}
